import React from 'react';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_ERROR,
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_ERROR,
  FORGOT_USERNAME,
  FORGOT_USERNAME_SUCCESS,
  FORGOT_USERNAME_ERROR,
} from '../actions/loginRecoveryActions';
import { FormattedMessage } from 'react-intl';
import { errorDisplay } from '../../utilities/general';
import { messageTypes } from '../../components/ui/AlertMessage';

/**
 * Middleware to send a password reset link to the provided email address
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to send the reset email
 */
export const forgotPassword = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_PASSWORD) {
    const body = { email: action.payload.email };
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].forgotPassword,
      onSuccess: FORGOT_PASSWORD_SUCCESS,
      onError: FORGOT_PASSWORD_ERROR,
      auth: '',
      body,
    };
    dispatch(showLoading());
    dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to handle a successful forgot-password request
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows a success message to the user
 */
export const forgotPasswordSuccess = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_PASSWORD_SUCCESS) {
    dispatch(hideLoading());
  }
};

/**
 * Middleware to handle a failed forgot-password request
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows an error message to the user
 */
export const forgotPasswordError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_PASSWORD_ERROR) {
    dispatch(hideLoading());
    const message = {
      message: (
        <FormattedMessage
          id="loginRecovery.forgotPasswordErrorText"
          defaultMessage="There was an error sending the password reset email."
          description="Forgot password error message"
        />
      ),
      error: errorDisplay(action.payload),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to submit the new password using the uid and token from the reset link
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to reset the password
 */
export const resetPassword = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === RESET_PASSWORD) {
    const body = {
      uid: action.payload.uid,
      token: action.payload.token,
      new_password: action.payload.newPassword,
    };
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].resetpassword,
      onSuccess: RESET_PASSWORD_SUCCESS,
      onError: RESET_PASSWORD_ERROR,
      auth: '',
      body,
    };
    dispatch(showLoading());
    dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to handle a successful password reset
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows a success message to the user
 */
export const resetPasswordSuccess = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === RESET_PASSWORD_SUCCESS) {
    dispatch(hideLoading());
    const message = {
      message: (
        <FormattedMessage
          id="loginRecovery.resetPasswordSuccessText"
          defaultMessage="Your password has been reset. You may now log in."
          description="Reset password success message"
        />
      ),
      messageType: messageTypes.success,
    };
    dispatch(addUiMsg(message));
    setTimeout(() => {
      window.location.href = '/index.php';
    }, 4000);
  }
};

/**
 * Middleware to handle a failed password reset
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows an error message to the user
 */
export const resetPasswordError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === RESET_PASSWORD_ERROR) {
    dispatch(hideLoading());
    const message = {
      message: (
        <FormattedMessage
          id="loginRecovery.resetPasswordErrorText"
          defaultMessage="There was an error resetting your password. The link may be invalid or expired."
          description="Reset password error message"
        />
      ),
      error: errorDisplay(action.payload),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to send a username recovery to the provided email address
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to send the recovery email
 */
export const forgotUsername = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_USERNAME) {
    const body = {
      email: action.payload.email,
    };
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].forgotUsername,
      onSuccess: FORGOT_USERNAME_SUCCESS,
      onError: FORGOT_USERNAME_ERROR,
      auth: '',
      body,
    };
    dispatch(showLoading());
    dispatch(apiRequest(body, meta));
  }
};



/**
 * Middleware to handle a successful forgot-username request
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows a success message to the user
 */
export const forgotUsernameSuccess = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_USERNAME_SUCCESS) {
    dispatch(hideLoading());
  }
};

 /**
 * Middleware to handle a failed forgot-username request
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Hides the loader bar
 * @fires dispatch#addUiMsg - Shows an error message to the user
 */
export const forgotUsernameError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === FORGOT_USERNAME_ERROR) {
    dispatch(hideLoading());
    const message = {
      message: (
        <FormattedMessage
          id="loginRecovery.forgotUsernameErrorText"
          defaultMessage="There was an error sending the recovery username email."
          description="Forgot username error message"
        />
      ),
      error: errorDisplay(action.payload),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};


export const loginRecoveryMdl = [
  forgotPassword,
  forgotPasswordSuccess,
  forgotPasswordError,
  resetPassword,
  resetPasswordSuccess,
  resetPasswordError,
  forgotUsername,
  forgotUsernameSuccess,
  forgotUsernameError,

];
